import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

const allWords = [
    "CAT", "DOG", "BAT", "PIG", "COW", "FOX", "OWL", "ANT", "BEE", "BUG",
    "YAK", "RAT", "HEN", "APE", "EMU", "EEL", "RAM", "DOE", "KIT", "CUB",
    "SUN", "SKY", "MUD", "WEB", "ICE", "OIL", "EGG", "SEA", "BAY", "FUR",
    "PET", "FIN", "DEN", "HAY", "TAN", "GEM", "SAP", "LEA", "NUT", "CAR",
    "BUS", "BOX", "CAP", "MAP", "PAN", "JAR", "PEN", "POT", "CUP", "BED",
    "BAG", "ZIP", "TOY", "TIN", "FAN", "VAN", "LID", "NET", "AXE", "KEY",
    "BAR", "RUG", "LOG", "BIN", "MAT", "LAP", "PAD", "MOP", "RIB", "BIB",
    "TAG", "PEG", "BOW", "TUB", "PIT", "HAT", "RUN", "HOP", "JOG", "FLY",
    "TRY", "GET", "HIT", "FIX", "ASK", "USE", "BUY", "CUT", "DRY", "PAY",
    "SAY", "WIN", "LAY", "NAP", "YAW", "ROW", "TIP", "NOD", "BUN", "TAP",
    "RAN", "SEE", "ACT", "JAM", "HUG", "DIG", "POP", "TIE", "WIG", "PAT",
    "NET", "MIX", "WAG", "MOM", "DAD", "BOY", "MAN", "KID", "PAL", "GAL",
    "BUD", "MAW", "SIS", "SON", "NAN", "BRO", "COZ", "LAD", "ZOO", "INN",
    "TOP", "LOW", "OUT", "CAB", "JET", "LOT", "LAB", "ATT", "AVE", "HOT",
    "COLD", "RED", "BIG", "FUN", "NEW", "OLD", "WET", "BAD", "MAD", "SAD",
    "GLU", "ICY", "ODD", "DIM", "RAW", "FAT", "NIL", "JOY", "HID", "RIP",
    "LIT", "COY", "NAG", "INK", "OFF", "YES", "ZEN", "ART", "JOB", "GAP",
    "KIN", "DOT", "JAB", "WAX", "FIG", "SOW", "WON", "CAN", "COT", "PIN",
    "PUN", "BAM", "BOP", "SIP", "VET", "WOW"
];

type WordSet = {
    letter: string;
    words: string[];
    correctWords: string[];
};

const getRandomLetter = () => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return alphabet[Math.floor(Math.random() * alphabet.length)];
};

const getRandomWordsSet = (): WordSet => {
    const letter = getRandomLetter();
    const matching = allWords.filter(word => word.startsWith(letter));
    const nonMatching = allWords.filter(word => !word.startsWith(letter));

    if (matching.length < 3) return getRandomWordsSet(); // retry if not enough matching words

    const selectedCorrect = matching.sort(() => 0.5 - Math.random()).slice(0, 3);
    const selectedIncorrect = nonMatching.sort(() => 0.5 - Math.random()).slice(0, 13);
    const mixed = [...selectedCorrect, ...selectedIncorrect].sort(() => 0.5 - Math.random());

    return { letter, words: mixed, correctWords: selectedCorrect };
};

const WordPickerGame: React.FC = () => {
    const [letterSet, setLetterSet] = useState<WordSet | undefined>();
    const [showImage, setShowImage] = useState(false);
    const [clickedWords, setClickedWords] = useState<{ [word: string]: string }>({});
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        resetSet();
        document.title = "Choose the Phonetic Word – LKG Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const resetSet = () => {
        const newSet = getRandomWordsSet();
        setLetterSet(newSet);
        setClickedWords({});
    };

    const handleClick = (word: string) => {
        const isAlreadyClicked = clickedWords[word];

        // 🔁 Toggle: If already clicked, remove it
        if (isAlreadyClicked) {
            const updated = { ...clickedWords };
            delete updated[word];
            setClickedWords(updated);
            return;
        }

        // ⛔ Limit: Don't allow more than 3 selections
        if (Object.keys(clickedWords).length >= 3) return;

        const isCorrect = letterSet?.correctWords.includes(word);
        const updated = {
            ...clickedWords,
            [word]: isCorrect ? "correct" : "incorrect",
        };

        setClickedWords(updated);

        // ✅ If 3 selected, check correctness
        const totalClicked = Object.keys(updated).length;
        if (totalClicked === 3) {
            const correctCount = Object.values(updated).filter(val => val === "correct").length;
            setShowImage(true);
            setTimeout(() => {
                if (correctCount === 3) {
                    const newSet = getRandomWordsSet();
                    setLetterSet(newSet);
                    setClickedWords({});
                }
                setShowImage(false);
            }, 3000);
        }
    };



    const getColor = (word: string) => {
        if (clickedWords[word] === "correct") return "success";
        if (clickedWords[word] === "incorrect") return "danger";
        return "medium";
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Boost phonics and word recognition in LKG kids with the Circle the Words activity on Mama’s Guidance—fun, engaging exercises to help children identify and circle correct phonetic words.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart seasons additionWord" style={{ marginTop: "10%" }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Choose the Phonetic Word</h1>

                                <p>
                                    Help your child grow into a confident reader with this <strong>Circle the Phonetic Word</strong> worksheet! Perfect for LKG students, this fun activity builds <strong>early phonics and word recognition</strong> through engaging image-word matching exercises.
                                </p>

                                <h2>📝 What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet presents <strong>simple pictures (like a sun, dog, cup, pen)</strong> alongside 2–3 word choices. Your child must <strong>circle the correct phonetic word (CVC word)</strong> that matches the picture.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize phonetic (CVC) words related to pictures</li>
                                    <li>Reinforce sound-letter relationships (phonics)</li>
                                    <li>Boost early reading, spelling, and word matching skills</li>
                                    <li>Develop confidence in identifying words visually and phonetically</li>
                                </ul>

                                <h2>🧩 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Go through the list of phonetic words with your child.</li>
                                    <li>Help them pronounce each word by sounding it out.</li>
                                    <li>Ask them to scan the letter grid and find the words.</li>
                                    <li>Encourage them to circle each word as they find it.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Matters</h2>
                                <p>
                                    Early phonetic awareness helps children <strong>decode unfamiliar words</strong> and begin reading independently. By pairing images with phonetic words, this worksheet strengthens the bridge between <strong>sounds, letters, and meaning</strong>.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Choose the Phonetic Word</h1>

                            <p>
                                Help your child grow into a confident reader with this <strong>Circle the Phonetic Word</strong> worksheet! Perfect for LKG students, this fun activity builds <strong>early phonics and word recognition</strong> through engaging image-word matching exercises.
                            </p>

                            <h2>📝 What’s in the Worksheet?</h2>
                            <p>
                                The worksheet presents <strong>simple pictures (like a sun, dog, cup, pen)</strong> alongside 2–3 word choices. Your child must <strong>circle the correct phonetic word (CVC word)</strong> that matches the picture.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize phonetic (CVC) words related to pictures</li>
                                <li>Reinforce sound-letter relationships (phonics)</li>
                                <li>Boost early reading, spelling, and word matching skills</li>
                                <li>Develop confidence in identifying words visually and phonetically</li>
                            </ul>

                            <h2>🧩 How to Use This Worksheet</h2>
                            <ul>
                                <li>Go through the list of phonetic words with your child.</li>
                                <li>Help them pronounce each word by sounding it out.</li>
                                <li>Ask them to scan the letter grid and find the words.</li>
                                <li>Encourage them to circle each word as they find it.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Matters</h2>
                            <p>
                                Early phonetic awareness helps children <strong>decode unfamiliar words</strong> and begin reading independently. By pairing images with phonetic words, this worksheet strengthens the bridge between <strong>sounds, letters, and meaning</strong>.
                            </p>
                        </article>
                        <h2><span style={{ color: "blue" }}>{letterSet?.letter}</span> for words</h2>
                        <div className="showWords" style={{ display: "flex", flexWrap: "wrap" }}>
                            {letterSet?.words.map((word, index) => (
                                <div key={index} className="ion-text-center">
                                    <div className={`background-blue ${getColor(word)}`}
                                        onClick={() => handleClick(word)}
                                    >
                                        {word}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: '150px' }}
                        className="img-fluid"
                        src={process.env.REACT_APP_LOADING_LINK}
                        alt="Well Done"
                    />
                </div>
            )}

        </>
    );
};

export default WordPickerGame;
